import {
  ActivityTracker,
  NamespaceLoggerAdapter,
  sendTelemetryEvent,
  getFluidDevOverrides,
  getLocalizationAttributes,
  enableUpToAudience,
  getRingForEnvironment
} from '@fluidx/utilities';
import {
  LoadedInfo,
  determineLayoutMode,
  ContainerEnvironment,
  CancellationError,
  HostContext
} from '@fluidx/office-fluid-types';
import { Activity, OWHEventSubCategories, OWHGenericEvent } from '../../Telemetry';
import { HostConfig, OfficeWebHostScope } from '../../OfficeWebHostTypes';
import { setupInitialIframeLayout } from '../../Iframe-shared/layout';
import { createBasicIframe } from '../../Iframe-shared/createBasicIframe';
import { attemptTokenPrefetch } from '../../Iframe-shared/attemptTokenPrefetch';
import {
  SourcelessIframeLoadOptions,
  SourcelessIframeLoadOptionsForTesting,
  LoadInSourcelessIframeResult,
  SourcelessIframeTestingOnlyOptions
} from '../../loadComponentInSourcelessIframe';
import * as devSettingsKeys from '../devOverrideKeys';
import { SourcelessInitializeParams } from '../SourcelessInitializeParams';
import { InitializeIframeMethodName } from '../Constants';
import { generateSrcdocForIframeInside } from '../srcdocGenerator';
import { getIframeIndexJsUrl } from './getIframeIndexJsUrl';
import { isFluidDebuggerEnabled } from '../../LoadHelpers/isFluidDebuggerEnabled';
import { InitializeConfig } from '../../Iframe-shared/InitializeConfig';
import { getAllDomainsForContainer } from '@ms/bohemia-codeloader';
import { getPackageName } from '../../getPackageName';
import { prepareAdditionalDriverProcessing } from './AdditionalDriverProcessing';
import { registerSettingsProvider } from '../../owhSettingsHandler';
import { mergeObjects } from '../../Utils';
import { npmVersionNumber } from '../../npm.package.version';

type AdditionalSourcelessIframeLoadInfo = {
  /** BootstrapperStartTime The moment we should consider the start of office-web-host execution (for perf analysis) */
  bootstrapperStartTime: number;

  /**
   * Whether the sourceless iframe boot path is being redirected via localStorage flags
   * from a regular "loadComponentInIframe" call
   */
  isRedirectingFromRegularIframe?: boolean;
};

/**
 * The internal implementation of "loadComponentInSourcelessIframe".
 * Most of the parameters are just pass-through-s from the original public-facing call.
 *
 * @param initializeConfig Tells the function which operation to perform (create or load), includes driver specific information needed for performing the required operations
 * @param componentDiv A div or a promise to a div that the component will be rendered into
 * @param scope An OfficeWebHostScope object that will be available to all containers/components loaded
 * @param config Additional information allowing a host to have richer integration with how components are initialized
 * @param options The sourceless iframe load options
 * @param additionalInfo Additional info to pass to the iframe
 */
export function loadComponentInSourcelessIframeInternal(
  initializeConfig: InitializeConfig,
  componentDiv: HTMLElement,
  scope: OfficeWebHostScope,
  config: HostConfig,
  options: SourcelessIframeLoadOptions,
  additionalInfo: AdditionalSourcelessIframeLoadInfo
): LoadInSourcelessIframeResult {
  // Before beginning, replace the logger with a namespaced logger,
  // and pass that through as the modified HostConfig
  const { logger: hostLogger, ...partialConfig } = config;
  const namespacedLogger =
    hostLogger &&
    new NamespaceLoggerAdapter('OfficeWebHost', hostLogger, {
      inSourcelessIframe: true,
      // We want a persistent property for identifying sessions which are using the replay driver so we can potentially ignore those sessions in perf/reliability queries.
      isReplay: isFluidDebuggerEnabled() || undefined,
      owhVersion: npmVersionNumber
    });

  const newConfig = {
    ...partialConfig,
    logger: namespacedLogger
  };

  // For compatibility with the office-fluid-container, which still expects a HostContext.audience value,
  // we set it to the same value that was passed on the HostConfig.environment
  const newHostContext = mergeObjects<HostContext>(scope.HostContext, {
    audience: partialConfig.environment?.audience || 'Production'
  });

  const newScopes = mergeObjects<OfficeWebHostScope>(scope, { HostContext: newHostContext });

  // We are registering the setting provider as soon as we reach this point
  // in order to make sure that we can access the settings anywhere from here down the stack.
  scope.SettingsProvider && registerSettingsProvider(scope.SettingsProvider);

  return loadInSourcelessIframeHelper(initializeConfig, componentDiv, newScopes, newConfig, options, additionalInfo);
}

// A helper function where the real "loadInSourcelessIframe" action takes place.
// By the this code is invoked, the inputs are expected to have been sufficiently massaged
// by the publicly-exposed "loadComponentInSourcelessIframeInternal", and initialization can proceed in earnest.
function loadInSourcelessIframeHelper(
  initializeConfig: InitializeConfig,
  componentDiv: HTMLElement,
  scope: OfficeWebHostScope,
  config: HostConfig,
  options: SourcelessIframeLoadOptions,
  additionalInfo: AdditionalSourcelessIframeLoadInfo
): LoadInSourcelessIframeResult {
  if (initializeConfig.type === 'Load') {
    attemptTokenPrefetch(scope.TokenProvider, initializeConfig.driverInfo);
  }

  const { logger } = config;

  // "loadComponent" activity tracker represents the full end-to-end loading of the component & container.
  // "bootIframe", meanwhile, just represents that an iframe was created and fired an "onload".
  // It does NOT represent the initialization call from within the iframe (that is captured
  // separately by an "iframeLoadAndInitialize" event that gets fired from within the iframe).
  const loadComponentActivityTracker = ActivityTracker.start(Activity.LoadComponentInIframe, logger, {
    addUserTiming: true
  });
  const bootIframeActivityTracker = ActivityTracker.start(Activity.IFrameBoot, logger, {
    addUserTiming: true
  });
  const iframeLoadStartTime = Date.now();

  const additionalDriverProcessing = prepareAdditionalDriverProcessing(initializeConfig, config, options);

  let keepGoingWithLoad = true;
  let disposeAlreadyCalledOnce = false;
  let iframe: HTMLIFrameElement | null;

  const onDispose: () => void = () => {
    if (disposeAlreadyCalledOnce) {
      return;
    }

    keepGoingWithLoad = false;
    disposeAlreadyCalledOnce = true;

    loadComponentActivityTracker.dispose();
    bootIframeActivityTracker.dispose();

    // Call the onDispose provided by host to do any custom cleanup needed
    // Host provided dispose could be responsible for pausing accidental logging for example on the CreateNew path or may have genuine log statements.
    // We don't want any logs to spill over the IframeDisposed signal, so make sure to call host provided dispose before IframeDispose event is logged
    const hostDispose = options.onDispose;
    hostDispose && hostDispose();

    if (logger) {
      sendTelemetryEvent(logger, {
        eventName: OWHGenericEvent.IframeDisposed,
        subCategory: OWHEventSubCategories.IframeDisposed
      });

      logger.dispose && logger.dispose();
    }

    // Set the iframe reference to null, but don't dispose of the actual iframe DOM.
    // We leave it to the host to clear the DOM of the component div (which will probably get
    // batched with other DOM or React DOM updates anyway).
    // See more in the "Disposing iframes" section of "docs\ecosystem\hosting\OfficeWebHost.md".
    iframe = null;
  };

  let resumeLoad: (() => void) | undefined;
  let resumeLoadTime: number | undefined;
  const loadedInfo = new Promise<LoadedInfo>((resolve, reject) => {
    const localization = getLocalizationAttributes();
    const layoutMode = config.componentRenderOptions?.layoutMode || determineLayoutMode(componentDiv);

    iframe = createBasicIframe(options.iframeTitle);
    setupInitialIframeLayout(layoutMode, iframe);

    const onIframeLoadOrResume = async () => {
      if (!keepGoingWithLoad) {
        return;
      }

      try {
        bootIframeActivityTracker.setResult(true);

        // By the time the onload fired, and since it's a sourceless iframe, contentWindow is not null
        const iframeWindow = iframe!.contentWindow!;

        const argsForIframe: SourcelessInitializeParams = {
          iframe: iframe!,
          initializeConfig,
          scope,
          config,
          options,
          layoutMode,
          iframeLoadStartTime,
          localization,
          bootstrapperStartTime: additionalInfo.bootstrapperStartTime,
          resumeLoadTime,
          isRedirectingFromRegularIframe: additionalInfo.isRedirectingFromRegularIframe,
          onDispose,
          cancellationErrorGenerator: (reason) => new CancellationError(reason),
          proceedWithInternalLoad: additionalDriverProcessing.proceedWithDriverLoad
        };

        // Call the specially-exposed initialization method, that was exposed on the Inside of the iframe
        const initMethod: (params: SourcelessInitializeParams) => Promise<LoadedInfo> = (iframeWindow as any)[
          InitializeIframeMethodName
        ];
        const loadResult: LoadedInfo = await initMethod(argsForIframe);

        runIfInTestingMode(
          (options) => options.codeToInject,
          (codeToInject) => injectScriptCodeIntoWindow(iframeWindow.document, codeToInject)
        );

        // Set up the loadComponentActivityTracker to resolve once the container and component
        // have loaded -- but do NOT wait for it to complete:
        Promise.all([loadResult.component, loadResult.container])
          .then(() => loadComponentActivityTracker.setResult(true))
          .catch((error) => loadComponentActivityTracker.setResult(false, {}, error));

        resolve(loadResult);
      } catch (e: any) {
        reject(e);
      }
    };

    if (options.partialLoadOnly) {
      const onIframeLoadPromise = new Promise<void>((resolveIframeOnloadPromise) => {
        iframe!.onload = () => {
          resolveIframeOnloadPromise();
          runIfInTestingMode(
            (options) => options.afterIframeOnload,
            (afterIframeOnload) => afterIframeOnload()
          );
        };
      });
      resumeLoad = () => {
        // If had already called "resume" once -- which we can tell by resumeLoadTime being set --
        // do nothing.
        if (resumeLoadTime) {
          return;
        }

        resumeLoadTime = Date.now();
        // Disabling the lint rule for no floating promises below.
        // The onIframeLoadPromise will never reject, and the onIframeLoadOrResume has a try/catch that
        // properly handles rejecting the outer promise in case of error (and thus also will never reject).
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onIframeLoadPromise.then(() => onIframeLoadOrResume());
      };
    } else {
      iframe.onload = onIframeLoadOrResume;
    }

    const preconnectUrls = [];

    // If we are loading a file, we can preconnect to some domains the driver tells us are essential for obtaining a snapshot
    const driverPreconnectDomains =
      initializeConfig.type === 'Load' ? initializeConfig.driverInfo.preconnectDomains : undefined;
    if (driverPreconnectDomains) {
      preconnectUrls.push(...driverPreconnectDomains);
    }

    // If we can derive the container code type from our inputs, we can also preconnect to any domains the code loader deems critical for boot performance
    let containerPackageName: string | undefined = undefined;
    if (initializeConfig.type === 'Load') {
      containerPackageName = initializeConfig.driverInfo?.containerCodeHint;
    } else if (initializeConfig.type === 'CreateFromCode' && initializeConfig.codeDetails) {
      containerPackageName = getPackageName(initializeConfig.codeDetails);
    }

    if (containerPackageName) {
      const deploymentRing = getRingForEnvironment(config.environment);
      preconnectUrls.push(
        // Task 5294949: Convert from ContainerEnvironment to DeploymentRing
        // Both types currently hold the same value minus Local that only exists on ContainerEnvironment
        ...getAllDomainsForContainer(containerPackageName, deploymentRing as ContainerEnvironment | undefined)
      );
    }

    const iframeBundleUrl =
      getFluidDevOverrides(devSettingsKeys.sourcelessIframeUrl) ||
      options.sourcelessIframeBundleUrl ||
      getIframeIndexJsUrl(
        getFluidDevOverrides(devSettingsKeys.sourcelessIframeFlavor) ||
          (options.useServiceDeliveredDriver ? 'serviceDeliveredDriver' : undefined)
      );

    iframe.srcdoc = generateSrcdocForIframeInside(
      [...(additionalDriverProcessing.scriptUrlsToPrepend || []), iframeBundleUrl],
      preconnectUrls
    );
    componentDiv.appendChild(iframe);
  });

  const getIframe: () => HTMLIFrameElement | undefined = () => iframe || undefined;

  return {
    loadedInfo,
    dispose: onDispose,
    resumeLoad,
    getIframe
  };

  /** Helper function that will run only if the option value is truthy AND if in a Test audience */
  function runIfInTestingMode<T>(
    testingValueFetcher: (forTestingOnly: SourcelessIframeTestingOnlyOptions) => T,
    callback: (passThroughValue: NonNullable<T>) => void
  ) {
    if (enableUpToAudience('Automation', scope.HostContext?.audience)) {
      const forTestingOnly = (options as SourcelessIframeLoadOptionsForTesting)._DoNotUse_ForTestingOnly;
      if (forTestingOnly) {
        const value = testingValueFetcher(forTestingOnly);
        if (value) {
          callback(value!);
        }
      }
    }
  }
}

function injectScriptCodeIntoWindow(iframeDocument: Document, content: string): void {
  const scriptElem = iframeDocument.createElement('script');
  // Note: using "createTextNode", any relevant HTML entities will be properly escaped
  // (e.g., "console.log('</script>')"" would have broken everything when inserting code
  // via srcdoc, but would be OK when inserted via "createTextNode").
  scriptElem.appendChild(document.createTextNode(content));
  iframeDocument.head.appendChild(scriptElem);
}

// cspell:ignore bootstrapper, prefetch, srcdoc, iframes, preconnect
